import { useMemo, useState } from "react";
import type {
  WidgetEvent,
  WidgetEventCta,
  WidgetEventTicketType,
  WidgetEventTicketOptionGroup,
} from "../types";
import { useCart, type CartItem } from "../state/cart";
import { createEventTicketHold, getCartToken } from "../state/booking";
import { computeCohostPairing, cohostNoticeText } from "../state/cohost";
import { pushToast } from "../state/toast";
import { optimizedImageUrl, optimizedSrcSet, IMG_WIDTHS } from "../lib/image";

// Detail view for one event. User picks quantities per ticket type, then
// "Add to booking" creates one event-ticket hold per non-zero line and
// pushes a cart item for each. Multi-type purchases land as multiple
// cart lines, matching the existing service-units pattern.
export function EventDetailPanel({
  event,
  onBack,
  onAdded,
  onCta,
}: {
  event: WidgetEvent;
  onBack: () => void;
  onAdded: () => void;
  /** In-widget CTA navigation (service_tab / enquiry). url CTAs are plain
   *  links handled inline. Optional — omitted CTAs just render nothing. */
  onCta?: (cta: WidgetEventCta) => void;
}) {
  const cart = useCart();
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  // Per-attendee option picks: selections[ticketTypeId][attendeeIndex][groupId] = optionId[]
  const [selections, setSelections] = useState<
    Record<string, Array<Record<string, string[]>>>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const types = event.ticket_types ?? [];
  // Co-hosting: if any ticket type pairs with another service, show a banner +
  // a soft CTA to book it (Phase A — kept for BOTH enforcement levels).
  // 'required' tickets additionally auto-guide to the service after add and
  // gate the cart checkout (Phase B, see addToBooking + CartPanel).
  const cohost = types.find((t) => t.cohost)?.cohost ?? null;

  // Total = SUM over attendees of max(0, base + that attendee's add-ons).
  // Mirror the server (create_event_ticket_hold floors each attendee line at 0)
  // so a discount option can't net a line negative or drag the total below what
  // the guest is actually charged. Deltas are recomputed server-side at hold time.
  const totalCents = useMemo(() => {
    let sum = 0;
    for (const t of types) {
      const qty = quantities[t.id] ?? 0;
      if (qty <= 0) continue;
      const perAtt = selections[t.id] ?? [];
      for (let i = 0; i < qty; i++) {
        const chosen = perAtt[i] ?? {};
        let line = t.price_cents;
        for (const g of t.option_groups ?? []) {
          for (const oid of chosen[g.id] ?? []) {
            const opt = g.options.find((o) => o.id === oid);
            if (opt) line += opt.price_delta_cents;
          }
        }
        sum += Math.max(0, line);
      }
    }
    return sum;
  }, [types, quantities, selections]);

  const totalQty = useMemo(
    () =>
      Object.values(quantities).reduce(
        (n, q) => n + (Number.isFinite(q) ? q : 0),
        0,
      ),
    [quantities],
  );

  // First unsatisfied required/min-max group blocks checkout (server also
  // enforces this).
  const optionError = useMemo(() => {
    for (const t of types) {
      const qty = quantities[t.id] ?? 0;
      if (qty <= 0 || !t.option_groups?.length) continue;
      const perAtt = selections[t.id] ?? [];
      for (let i = 0; i < qty; i++) {
        const chosen = perAtt[i] ?? {};
        for (const g of t.option_groups) {
          if (g.options.length === 0) continue;
          const n = (chosen[g.id] ?? []).length;
          if (n < g.min_select) return `Choose ${g.name} for each ${t.name}.`;
          if (n > g.max_select) return `Too many choices for ${g.name}.`;
        }
      }
    }
    return null;
  }, [types, quantities, selections]);

  const remaining =
    event.total_capacity !== null
      ? Math.max(0, event.total_capacity - event.sold_count)
      : null;
  const soldOut = remaining !== null && remaining === 0;

  function setQty(t: WidgetEventTicketType, qty: number) {
    const next = Math.max(0, qty);
    setQuantities((prev) => ({ ...prev, [t.id]: next }));
    // Resize this type's per-attendee slots, seeding sensible defaults.
    setSelections((prev) => {
      const cur = prev[t.id] ?? [];
      const resized: Array<Record<string, string[]>> = [];
      for (let i = 0; i < next; i++) resized.push(cur[i] ?? defaultSelection(t));
      return { ...prev, [t.id]: resized };
    });
  }

  function setChoice(
    t: WidgetEventTicketType,
    attendee: number,
    groupId: string,
    optionIds: string[],
  ) {
    setSelections((prev) => {
      const cur = (prev[t.id] ?? []).slice();
      cur[attendee] = { ...(cur[attendee] ?? {}), [groupId]: optionIds };
      return { ...prev, [t.id]: cur };
    });
  }

  async function addToBooking() {
    setError(null);
    if (totalQty === 0) {
      setError("Pick at least one ticket.");
      return;
    }
    if (optionError) {
      setError(optionError);
      return;
    }
    setSubmitting(true);

    const lines = types
      .map((t) => ({ type: t, qty: quantities[t.id] ?? 0 }))
      .filter((l) => l.qty > 0);

    const cartToken = getCartToken();
    for (const line of lines) {
      // Flatten this line's per-attendee picks to option-id arrays for the hold.
      const perAtt = selections[line.type.id] ?? [];
      const lineSelections: string[][] = [];
      for (let i = 0; i < line.qty; i++) {
        const chosen = perAtt[i] ?? {};
        lineSelections.push(
          (line.type.option_groups ?? []).flatMap((g) => chosen[g.id] ?? []),
        );
      }

      const hold = await createEventTicketHold({
        eventId: event.id,
        ticketTypeId: line.type.id,
        quantity: line.qty,
        guest: { name: null, email: null, phone: null },
        cartToken,
        selections: lineSelections,
      });
      if (!hold.ok) {
        setError(holdErrorMessage(hold.reason, line.type.name));
        setSubmitting(false);
        return;
      }
      // Price from the server-computed hold amount so the cart matches payment.
      const unitPrice = hold.amount_cents / 100 / line.qty;
      cart.add({
        serviceId: event.service_id ?? event.id,
        serviceName: event.name,
        serviceType: "event_ticket",
        unitId: line.type.id,
        unitName: line.type.name,
        description: `${event.name} - ${line.type.name}`,
        quantity: line.qty,
        unitPrice,
        currency: line.type.currency,
        brandId: null,
        brandName: null,
        metadata: {
          source: "event",
          event_id: event.id,
          ticket_type_id: line.type.id,
          booking_id: hold.booking_id,
          starts_at: event.starts_at,
          ends_at: event.ends_at,
          venue_location: event.venue_location,
          requires_full_payment: true,
          amount_cents: hold.amount_cents,
          selections_summary: summariseChoices(line.type, lineSelections),
          // Phase B: stamp the requirement on the line so the cart can
          // pair it with a sibling booking (gate + price display) without
          // re-fetching config.
          cohost: line.type.cohost ?? null,
        },
      });
    }

    setSubmitting(false);

    // ── Co-hosting auto-guide (Phase B) ──
    // A just-added ticket whose cohost.enforcement === 'required' with no
    // qualifying sibling booking in the cart: instead of opening the cart
    // drawer, navigate the guest straight into the required service's
    // booking flow (same navigation as the soft CTA banner) + toast the
    // notice. The persistent shell banner + cart gate keep nudging until
    // the sibling is added. `cart.items` here is the pre-add snapshot —
    // exactly what pairing needs as existing lines; the lines just added
    // are appended as placeholders (matching server "earliest first").
    const pendingPrefix = "__vt_pending_";
    const justAdded: CartItem[] = lines
      .filter((l) => l.type.cohost)
      .map((l, i) => ({
        id: `${pendingPrefix}${i}`,
        serviceId: event.service_id ?? event.id,
        serviceName: event.name,
        serviceType: "event_ticket",
        unitId: l.type.id,
        unitName: l.type.name,
        description: `${event.name} - ${l.type.name}`,
        quantity: l.qty,
        unitPrice: null,
        currency: l.type.currency,
        brandId: null,
        brandName: null,
        metadata: {
          source: "event",
          ticket_type_id: l.type.id,
          cohost: l.type.cohost,
        },
      }));
    const firstUnmet =
      justAdded.length > 0
        ? computeCohostPairing([...cart.items, ...justAdded]).unmet.find((u) =>
            u.itemId.startsWith(pendingPrefix),
          )
        : undefined;
    if (firstUnmet && onCta) {
      pushToast(cohostNoticeText(firstUnmet.cohost, event.name), "info");
      onCta({
        label: "cohost",
        kind: "service_tab",
        target: firstUnmet.cohost.required_service_slug,
      });
      return;
    }
    onAdded();
  }

  return (
    <div className="vt-event-detail">
      <button
        type="button"
        className="vt-event-back"
        onClick={onBack}
        aria-label="Back to events"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          width={14}
          height={14}
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        All events
      </button>

      {event.hero_image_url && (
        <div className="vt-event-detail-hero" aria-hidden>
          <img
            className="vt-cover-img"
            src={optimizedImageUrl(event.hero_image_url, { width: 1280 })}
            srcSet={optimizedSrcSet(event.hero_image_url, IMG_WIDTHS.HERO)}
            sizes="100vw"
            alt=""
            decoding="async"
            fetchPriority="high"
          />
        </div>
      )}

      <div className="vt-event-detail-meta">
        <span className="vt-event-detail-date">
          {formatDateRange(event.starts_at, event.ends_at)}
        </span>
        {event.venue_location && (
          <span className="vt-event-detail-venue">{event.venue_location}</span>
        )}
      </div>
      <h2 className="vt-event-detail-name">{event.name}</h2>
      {event.summary && (
        <p className="vt-event-detail-summary">{event.summary}</p>
      )}
      {event.description && (
        <div className="vt-event-detail-description">
          {event.description.split(/\n+/).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      )}

      {event.instructions && (
        <div className="vt-event-detail-instructions">
          {event.instructions.split(/\n+/).map((para, i) => (
            <p key={i}>{para}</p>
          ))}
        </div>
      )}

      {(event.cta_buttons?.length ?? 0) > 0 && (
        <div className="vt-event-detail-ctas">
          {event.cta_buttons!.map((c, i) =>
            c.kind === "url" ? (
              <a
                key={i}
                className="vt-cta-inline"
                href={c.target}
                target="_blank"
                rel="noreferrer"
              >
                {c.label}
              </a>
            ) : (
              <button
                key={i}
                type="button"
                className="vt-cta-inline"
                onClick={() => onCta?.(c)}
              >
                {c.label}
              </button>
            ),
          )}
        </div>
      )}

      <h3 className="vt-event-tickets-heading">Tickets</h3>
      {types.length === 0 ? (
        <p className="vt-event-empty-types">
          No ticket types are configured for this event yet.
        </p>
      ) : (
        <ul className="vt-event-detail-tickets">
          {types.map((t) => (
            <TicketTypeRow
              key={t.id}
              type={t}
              quantity={quantities[t.id] ?? 0}
              selections={selections[t.id] ?? []}
              onChange={(q) => setQty(t, q)}
              onChoiceChange={(attendee, groupId, ids) =>
                setChoice(t, attendee, groupId, ids)
              }
              disabled={soldOut || submitting}
            />
          ))}
        </ul>
      )}

      {error && (
        <div className="vt-event-error" role="alert">
          {error}
        </div>
      )}

      {cohost && (
        <div className="vt-event-cohost">
          <p className="vt-event-cohost-text">
            {cohost.prompt_text ||
              (cohost.charge_mode === "separate_paid"
                ? `Pair your tickets with a table at ${cohost.required_service_name}.`
                : `Your ticket includes a table at ${cohost.required_service_name}.`)}
          </p>
          <button
            type="button"
            className="vt-cta-inline"
            onClick={() =>
              onCta?.({
                label: "cohost",
                kind: "service_tab",
                target: cohost.required_service_slug,
              })
            }
          >
            Book a table at {cohost.required_service_name} &rarr;
          </button>
        </div>
      )}

      <div className="vt-event-detail-footer">
        <div className="vt-event-detail-total">
          <span className="vt-event-detail-total-label">Total</span>
          <span className="vt-event-detail-total-value">
            {formatPrice(totalCents, currencyOf(event, types))}
          </span>
        </div>
        <button
          type="button"
          className="vt-cta"
          onClick={addToBooking}
          disabled={soldOut || submitting || totalQty === 0}
        >
          {soldOut
            ? "Sold out"
            : submitting
              ? "Adding…"
              : totalQty === 0
                ? "Add to booking"
                : `Add ${totalQty} to booking`}
        </button>
      </div>
    </div>
  );
}

function TicketTypeRow({
  type,
  quantity,
  selections,
  onChange,
  onChoiceChange,
  disabled,
}: {
  type: WidgetEventTicketType;
  quantity: number;
  selections: Array<Record<string, string[]>>;
  onChange: (q: number) => void;
  onChoiceChange: (attendee: number, groupId: string, ids: string[]) => void;
  disabled: boolean;
}) {
  const max = type.quantity_available ?? 99;
  return (
    <li className="vt-event-detail-ticket-row">
      <div className="vt-event-detail-ticket">
        <div className="vt-event-detail-ticket-info">
          <div className="vt-event-detail-ticket-name">{type.name}</div>
          {type.description && (
            <div className="vt-event-detail-ticket-desc">{type.description}</div>
          )}
          <div className="vt-event-detail-ticket-price">
            {formatPrice(type.price_cents, type.currency)}
          </div>
        </div>
        <div className="vt-qty-control">
          <button
            type="button"
            aria-label={`Decrease ${type.name} quantity`}
            onClick={() => onChange(quantity - 1)}
            disabled={disabled || quantity === 0}
          >
            &minus;
          </button>
          <span className="vt-qty-value" aria-live="polite">
            {quantity}
          </span>
          <button
            type="button"
            aria-label={`Increase ${type.name} quantity`}
            onClick={() => onChange(quantity + 1)}
            disabled={disabled || quantity >= max}
          >
            +
          </button>
        </div>
      </div>

      {quantity > 0 && (type.option_groups?.length ?? 0) > 0 && (
        <div className="vt-event-ticket-options">
          {Array.from({ length: quantity }, (_, i) => (
            <div key={i} className="vt-event-ticket-attendee">
              {quantity > 1 && (
                <div className="vt-event-attendee-label">
                  {type.name} {i + 1}
                </div>
              )}
              {(type.option_groups ?? []).map((g) => (
                <OptionGroupField
                  key={g.id}
                  group={g}
                  currency={type.currency}
                  chosen={selections[i]?.[g.id] ?? []}
                  disabled={disabled}
                  onChange={(ids) => onChoiceChange(i, g.id, ids)}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

function OptionGroupField({
  group,
  currency,
  chosen,
  disabled,
  onChange,
}: {
  group: WidgetEventTicketOptionGroup;
  currency: string;
  chosen: string[];
  disabled: boolean;
  onChange: (ids: string[]) => void;
}) {
  // Show a positive add-on as "(+R40)" and a discount option as "(-R40)"
  // (deltas can be negative); a zero delta shows no price suffix.
  const label = (name: string, delta: number) =>
    delta
      ? `${name} (${delta > 0 ? "+" : "-"}${formatPrice(Math.abs(delta), currency)})`
      : name;
  return (
    <div className="vt-event-option-group">
      <label className="vt-event-option-label">
        {group.name}
        {group.min_select >= 1 && <span className="vt-event-option-req"> *</span>}
      </label>
      {group.max_select === 1 ? (
        <select
          className="vt-event-option-select"
          value={chosen[0] ?? ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
        >
          {group.min_select === 0 && <option value="">None</option>}
          {group.options.map((o) => (
            <option key={o.id} value={o.id}>
              {label(o.name, o.price_delta_cents)}
            </option>
          ))}
        </select>
      ) : (
        <div className="vt-event-option-checks">
          {group.options.map((o) => {
            const on = chosen.includes(o.id);
            return (
              <label key={o.id} className="vt-event-option-check">
                <input
                  type="checkbox"
                  checked={on}
                  disabled={disabled || (!on && chosen.length >= group.max_select)}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? [...chosen, o.id]
                        : chosen.filter((x) => x !== o.id),
                    )
                  }
                />
                {label(o.name, o.price_delta_cents)}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Default a new attendee slot: required single-choice groups get their first
// option so the total + validation start valid; everything else empty.
function defaultSelection(t: WidgetEventTicketType): Record<string, string[]> {
  const sel: Record<string, string[]> = {};
  for (const g of t.option_groups ?? []) {
    sel[g.id] =
      g.min_select >= 1 && g.max_select === 1 && g.options[0]
        ? [g.options[0].id]
        : [];
  }
  return sel;
}

// Readable per-attendee summary for the cart line (client-side display only;
// the authoritative record is stored server-side by the hold).
function summariseChoices(
  t: WidgetEventTicketType,
  lineSelections: string[][],
): string[] {
  const names = new Map<string, string>();
  for (const g of t.option_groups ?? [])
    for (const o of g.options) names.set(o.id, o.name);
  return lineSelections.map((ids) =>
    ids.map((id) => names.get(id) ?? "").filter(Boolean).join(", "),
  );
}
function holdErrorMessage(reason: string, typeName: string): string {
  switch (reason) {
    case "event_not_bookable":
    case "event_sales_not_open":
      return "This event isn't on sale yet.";
    case "event_sales_closed":
      return "Sales for this event have closed.";
    case "event_capacity_exceeded":
      return "Not enough tickets remaining for the whole event.";
    case "ticket_type_sold_out":
      return `${typeName} is sold out.`;
    case "ticket_type_disabled":
    case "ticket_type_not_found":
      return `${typeName} is no longer available.`;
    case "ticket_type_sales_not_open":
      return `${typeName} sales haven't opened yet.`;
    case "ticket_type_sales_closed":
      return `${typeName} sales have closed.`;
    case "invalid_quantity":
      return "Pick at least one ticket.";
    case "invalid_option_selection":
    case "option_selection_out_of_range":
      return "Please check your selections and try again.";
    default:
      return reason || "Couldn't reserve tickets.";
  }
}

function currencyOf(
  event: WidgetEvent,
  types: WidgetEventTicketType[],
): string {
  return types[0]?.currency ?? event.currency ?? "ZAR";
}

function formatPrice(cents: number, currency: string): string {
  if (cents === 0) return "Free";
  const rands = cents / 100;
  const str =
    cents % 100 === 0
      ? Math.round(rands).toLocaleString()
      : rands.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  return `${currencySymbol(currency)}${str}`;
}

function currencySymbol(code: string): string {
  switch (code) {
    case "ZAR":
      return "R ";
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    default:
      return code ? `${code} ` : "";
  }
}

function formatDateRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();
  const datePart = start.toLocaleDateString("en-ZA", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const timeFmt: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };
  if (sameDay) {
    return `${datePart} · ${start.toLocaleTimeString("en-ZA", timeFmt)} – ${end.toLocaleTimeString("en-ZA", timeFmt)}`;
  }
  return `${datePart} – ${end.toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "short" })}`;
}
